/**
 * React context for data that is used repeatedly throughout the application. Namely, Client details and a list of the Client's Users.
 * This prevents needing to make repeated queries to get those details in multiple components, and prevents needing to prop drill throughout the component tree.
 * In this context (pun unintended), Client refers to a SummIT Security customer.
 */

import { type JSX, createContext, useCallback, useContext, useEffect, useState } from 'react';

import { ClientDetailsApi } from 'Api/Client/ClientDetailsApi';
import { TagsApi } from 'Api/Tags/TagsApi';
import { UsersApi } from 'Api/Users/UsersApi';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { ClientDetailsModel, License } from 'Models/ClientDetailsModel';
import { DisplayableTag, TagCategory } from 'Models/Tags';
import { GroupedOptions } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';

// TODO: Consider memoizing an entire `UserResponse` object for the logged-in user, dependent on all users being loaded and fetching the user's access token from local storage.
/**
 * @param clientDetails The Client's (customer's) information, including the licenses.
 * @param convertTagIdsToDisplayableTags A function that takes an array of Tag IDs and returns an array of DisplayableTag objects. This is used to convert Tag IDs to their corresponding metadata for display purposes.
 * @param getTagCategoriesGroupedOptions A function that returns a list of Tag Categories and their Tags in a format suitable for use in drop-down menus.
 * @param getTagsMap A function that returns a map of Tag IDs to Tag metadata. This allows various components to look up Tag details by ID.
 * @param refreshTags A function that refreshes the Tag Categories and Tags from the API. This is used to update the Tag hierarchy when it changes.
 * @param refreshUsers A function that refreshes the list of Users from the API. This is used to update the list of Users when it changes.
 * @param tagCategories Stores the "raw" Tag hierarchy from the API. Different parts of the application need to use Tags in different formats, so we cache them in this format and have other functions convert them to other formats.
 * @param users Stores the list of Users from the API.
 */
interface CachedDataContextValue {
    clientDetails: ClientDetailsModel;
    convertTagIdsToDisplayableTags: (tagIds: string[]) => DisplayableTag[];
    getTagCategoriesGroupedOptions: () => GroupedOptions[];
    getTagsMap: () => Map<string, DisplayableTag>;
    refreshTags: () => void;
    refreshUsers: () => void;
    tagCategories: TagCategory[];
    users: UserResponse[];
}
export const CachedDataContext = createContext<CachedDataContextValue | undefined>(undefined);

interface CachedDataProviderProps {
    children: React.ReactNode;
    clientDetailsApi: ClientDetailsApi;
    tagsApi: TagsApi;
    usersApi: UsersApi;
}
export const CachedDataProvider = (props: CachedDataProviderProps): JSX.Element => {
    const [clientDetails, setClientDetails] = useState<ClientDetailsModel>();
    const [error, setError] = useState<Error>();
    const [tagCategories, setTagCategories] = useState<TagCategory[]>();
    const [users, setUsers] = useState<UserResponse[]>();

    /** Get details for the Client from the API. */
    useEffect(() => {
        const getClientDetails = async (): Promise<void> => {
            try {
                const clientDetailsResponse = await props.clientDetailsApi.getClientDetails();
                setClientDetails(clientDetailsResponse.data);
            } catch (error) {
                setError(error);
            }
        };
        getClientDetails();
    }, [props.clientDetailsApi]);

    /** Get the Client's hierarchy of sorted Tag Categories and Tags from the API. */
    const getTags = useCallback(() => {
        const getTags = async (): Promise<void> => {
            try {
                const response = await props.tagsApi.getTags();
                const tagCategories = response.data.category_set;
                tagCategories.sort((a, b) => (a.title > b.title ? 1 : -1));
                tagCategories.forEach((tagCategory) => tagCategory.tags.sort((a, b) => (a.title > b.title ? 1 : -1)));
                setTagCategories(tagCategories);
            } catch (error) {
                setError(error);
            }
        };
        getTags();
    }, [props.tagsApi]);

    /** Get a list of Users from the API. */
    const getUsers = useCallback(() => {
        const getUsers = async (): Promise<void> => {
            try {
                const usersResponse = await props.usersApi.getUsers();
                const users = usersResponse.data;
                setUsers(users);
            } catch (error) {
                setError(error);
            }
        };
        getUsers();
    }, [props.usersApi]);

    useEffect(() => {
        getTags();
        getUsers();
    }, [getTags, getUsers]);

    if (error) {
        return <Text>{error.message}</Text>;
    } else if (clientDetails === undefined || tagCategories === undefined || users === undefined) {
        return <Placeholder />;
    } else {
        /** Returns a list of Tags converted to GroupedOptions. This allows Tags to be displayed in drop-down menus. */
        const getTagCategoriesGroupedOptions = (): GroupedOptions[] => {
            const tagCategoriesGroupedOptions = tagCategories.map((tagCategory) => ({
                label: tagCategory.title,
                options: tagCategory.tags.map((tag) => ({
                    groupId: tagCategory.title,
                    label: tag.title,
                    value: tag.id!, // TODO: `!` should not be needed.
                })),
            }));
            return tagCategoriesGroupedOptions;
        };

        /** Returns a map of Tag IDs to Tag metadata. This allows various components to look up Tag details by ID. */
        const getTagsMap = (): Map<string, DisplayableTag> => {
            const tagsMap = new Map<string, DisplayableTag>();
            tagCategories.forEach((tagCategory) => {
                tagCategory.tags.forEach((tag) => {
                    tagsMap.set(tag.id!, { id: tag.id!, title: tag.title, category_color: tagCategory.color, category_title: tagCategory.title });
                });
            });
            return tagsMap;
        };

        /** Given an array of Tag IDs, reference them against the Tag Category hierarchy in state to return a corresponding array of Tag metadata. */
        const convertTagIdsToDisplayableTags = (tagIds: string[]): DisplayableTag[] => {
            const tagsMap = getTagsMap();
            return tagIds.map((tagId) => tagsMap.get(tagId)!);
        };

        return <CachedDataContext.Provider value={{ clientDetails: clientDetails, convertTagIdsToDisplayableTags: convertTagIdsToDisplayableTags, getTagsMap: getTagsMap, getTagCategoriesGroupedOptions: getTagCategoriesGroupedOptions, refreshTags: getTags, refreshUsers: getUsers, tagCategories: tagCategories, users: users }}>{props.children}</CachedDataContext.Provider>;
    }
};

export const useCachedData = (): CachedDataContextValue => {
    const context = useContext(CachedDataContext);
    if (context === undefined) {
        throw new Error('useCachedData must be used within a CachedDataProvider.');
    }
    return context;
};

/**
 * Compare a list of licenses that a Client has with another list of authorized licenses. Used to determine if a Client should have access to some SummIT Security feature.
 * If simply wrapping a component for conditional rendering, use <ClientLicenseComponent>.
 * @param authorizedLicenses list of licenses to compare the Client's licenses against.
 * @param clientLicenses list of licenses that the Client has.
 * @returns true if the Client has at least one license in authorizedLicenses, else false.
 */
export const clientHasAuthorizedLicense = (authorizedLicenses: License[], clientLicenses: License[]): boolean => {
    return clientLicenses.some((license) => authorizedLicenses.includes(license));
};
